import { MarketplaceAnalyticsContext } from '@atlassian/mpac-analytics';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { AppHeader, ResponsiveTabs } from '@atlassian/mpac-ui';
import { MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import React from 'react';
import { AppListingTab, ELEMENT_ID, MAX_VISIBLE_TABS_CONFIG } from '../../constants';
import { makeAppHeaderProps } from '../../utils';
import { AnimationContainer, HostingSelectContainerV1, HostingSelectContainerV2, ListingContainer } from './AppListing.styles';
import transformAppListing from './AppListing.transform';
import { compatibleWithCloudSupportedProduct } from './AppListing.utils';
import { makeHeaderLozenges } from './HeaderLozenges';
import HostingSelect, { HostingSelectV2 } from './HostingSelect';
import { makeAppListingTabs } from './makeAppListingTabs';
import ProductCompatibilitySummary from './ProductCompatibilitySummary';
import { AppStructuredData } from './StructuredData';
import AppListingV2 from './v2/AppListingV2';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export const selectedTabWithFallback = (tabs, selectedTab, onTabSelect) => {
  const tabSelected = tabs.find(tab => tab.key === selectedTab);

  // If we didn't find the selected tab key in the current list of available
  // tabs, fallback to the first tab, and notify that tab has changed
  if (!tabSelected) {
    onTabSelect(tabs[0].key);
    return tabs[0];
  }
  return tabSelected;
};

/* eslint-disable complexity */
// eslint-disable-next-line max-lines-per-function
const AppListing = props => {
  var _appListing$programs, _appListing$programs$, _props$components, _props$components2, _appListing$programs2, _appListing$programs3, _props$components3;
  const {
    showAppListingTrustTab,
    showNewJiraSoftwareLabel,
    enableAetherAppListingPage
  } = useStatsig();
  const showNewJiraSoftwareLabelFlag = showNewJiraSoftwareLabel();
  const showAetherAppListingPage = enableAetherAppListingPage();
  if (!props.appDetails) return null;
  const {
    appDetails,
    appDetailsFromSearchResponse,
    links,
    headerInjection,
    selectedHosting,
    selectedTab,
    onTabSelect,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    cloudMigration,
    loginHref,
    user,
    onHostingSelectOpen,
    onHostingOptionClick,
    onSupportLinkClick = () => null,
    onMediaViewerOpen = () => null,
    onWatchAppToggle = () => null,
    onSeeMoreReviewsClick = () => null,
    onArtifactDownloadClick = () => null,
    onContactPartnerClick = () => null,
    onCloudMigrationResourceLinksClick = () => null,
    onMigrationLozengeClick = () => null,
    onPricingCalculatorInstallCTAClick = () => null,
    showHeaderLozenges = false,
    showHeaderProductCompatibilities = false,
    showInstallationCount = false,
    showInstallationTab = false,
    serverEolStartDate = '',
    showIntegrationsDetailsSection = false,
    showPricingTab = false,
    showResourcesSection = false,
    showReviewsTab = false,
    showVersionsTab = false,
    showCloudMigrationLozenge = false,
    openMarketplaceLinksInNewTab = true,
    feedbackGatewayBaseUrl,
    feedbackCollectorApiKeys,
    isServerAppEos
  } = props;
  const appListing = transformAppListing({
    appDetails,
    appDataFromSearch: appDetailsFromSearchResponse === null || appDetailsFromSearchResponse === void 0 ? void 0 : appDetailsFromSearchResponse.appData,
    mediaImageLink: links.mediaImage,
    partnerDetailsLink: links.partnerDetails,
    appVersionHistoryLink: links.appVersionHistory,
    artifactDownloadLink: links.artifactDownload,
    descriptorDownloadLink: links.descriptorDownload,
    licensePurchaseLink: links.licensePurchase,
    trialLicenseActivationLink: links.trialLicenseActivation,
    appIconLink: links.appIcon,
    hosting: selectedHosting
  });
  const appCompatibleWithDcEnabledProduct = MarketplaceAppUtils.maybeDCCompatible(appListing.productHostingOptions, appListing.serverCompatibleProducts);
  const appCompatibleWithCloudSupportedProduct = compatibleWithCloudSupportedProduct(appListing.compatibleProducts);
  const showServerEolMessage = selectedHosting === AtlassianProductHostingType.Server && !MarketplaceAppUtils.isExcludedServerApp(appListing.compatibleProducts);
  const alternateCloudListingLink = cloudMigration !== null && cloudMigration !== void 0 && cloudMigration.alternateCloudAppKey ? links.appDetails.expand({
    appKey: cloudMigration.alternateCloudAppKey
  }) : null;
  let hostingSelect;
  if (props.showHostingSelect) {
    if (showAetherAppListingPage) {
      hostingSelect = /*#__PURE__*/_jsx(HostingSelectContainerV2, {
        children: /*#__PURE__*/_jsx(HostingSelectV2, {
          productHostingOptions: appListing.productHostingOptions,
          selected: props.selectedHosting,
          appCompatibleWithDcEnabledProduct: appCompatibleWithDcEnabledProduct,
          appCompatibleWithCloudSupportedProduct: appCompatibleWithCloudSupportedProduct,
          onChange: props.onHostingChange,
          partnerSupportUrl: appListing.partner.support.url,
          alternateCloudListingLink: alternateCloudListingLink,
          onContactPartnerClick: onContactPartnerClick,
          onOpen: onHostingSelectOpen,
          onHostingOptionClick: onHostingOptionClick
        })
      });
    } else {
      hostingSelect = /*#__PURE__*/_jsx(HostingSelectContainerV1, {
        children: /*#__PURE__*/_jsx(HostingSelect, {
          productHostingOptions: appListing.productHostingOptions,
          selected: props.selectedHosting,
          appCompatibleWithDcEnabledProduct: appCompatibleWithDcEnabledProduct,
          appCompatibleWithCloudSupportedProduct: appCompatibleWithCloudSupportedProduct,
          onChange: props.onHostingChange,
          partnerSupportUrl: appListing.partner.support.url,
          alternateCloudListingLink: alternateCloudListingLink,
          onContactPartnerClick: onContactPartnerClick,
          onOpen: onHostingSelectOpen,
          onHostingOptionClick: onHostingOptionClick
        })
      });
    }
  }
  const handleTabChange = tabData => {
    const tabKey = tabData && tabData.key;
    if (typeof onTabSelect === 'function') {
      onTabSelect(tabKey);
    }
  };
  const isAppCloudFortified = !!((_appListing$programs = appListing.programs) !== null && _appListing$programs !== void 0 && (_appListing$programs$ = _appListing$programs.cloudFortified) !== null && _appListing$programs$ !== void 0 && _appListing$programs$.isStatusApproved);
  const hasCloudFortifiedBadge = isAppCloudFortified;
  const appHeaderDetails = makeAppHeaderProps(appListing, appDetailsFromSearchResponse);
  const appRating = appHeaderDetails.ratings;
  const tabs = makeAppListingTabs({
    appListing,
    selectedHosting,
    showAetherAppListingPage,
    showInstallationTab,
    showIntegrationsDetailsSection,
    showResourcesSection,
    showCertificationSection: hasCloudFortifiedBadge,
    showReviewsTab,
    showVersionsTab,
    showPricingTab,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    loginHref,
    user,
    onSupportLinkClick,
    onMediaViewerOpen,
    onWatchAppToggle,
    onSeeMoreReviewsClick,
    onArtifactDownloadClick,
    cloudMigration,
    onCloudMigrationResourceLinksClick,
    onPricingCalculatorInstallCTAClick,
    showAppListingTrustTab: showAppListingTrustTab() || showAetherAppListingPage,
    appRating,
    showServerEolMessage: !!showServerEolMessage,
    serverEolStartDate,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
    isServerAppEos,
    onTabSelect
  });
  const tabSelected = selectedTabWithFallback(tabs, selectedTab, onTabSelect);
  const supportTabLink = UrlUtils.updateQueryParam(window.location.href, 'tab', AppListingTab.Support);
  const iconLink = {
    expand: () => appListing.logo
  };
  const Container = showAetherAppListingPage ? ListingContainer : 'div';
  return /*#__PURE__*/_jsx(MarketplaceAnalyticsContext, {
    properties: {
      source: 'appListingScreen'
    },
    attributes: {
      deploymentType: appListing.deploymentType,
      hasCloudFortifiedBadge
    },
    children: /*#__PURE__*/_jsxs(Container, {
      children: [/*#__PURE__*/_jsx(AnimationContainer, {
        children: (_props$components = props.components) === null || _props$components === void 0 ? void 0 : _props$components.beforeHeader
      }), showAetherAppListingPage ? /*#__PURE__*/_jsx(AppListingV2, {
        appHeaderDetails: appHeaderDetails,
        appRatingLink: links.appRating,
        appIconLink: iconLink,
        bottomSeparator: false,
        headerInjection: headerInjection,
        hostingSelect: hostingSelect,
        afterHeader: (_props$components2 = props.components) === null || _props$components2 === void 0 ? void 0 : _props$components2.afterHeader,
        isAppCloudFortified: isAppCloudFortified,
        showInstallationCount: showInstallationCount,
        openMarketplaceLinksInNewTab: openMarketplaceLinksInNewTab,
        bugBountyParticipant: (_appListing$programs2 = appListing.programs) === null || _appListing$programs2 === void 0 ? void 0 : _appListing$programs2.bugBountyParticipant,
        compatibilitySummary: showHeaderProductCompatibilities ? /*#__PURE__*/_jsx(ProductCompatibilitySummary, {
          cloudCompatibleProducts: appListing.cloudCompatibleProducts,
          serverCompatibleProducts: appListing.serverCompatibleProducts,
          dataCenterCompatibleProducts: appListing.dataCenterCompatibleProducts,
          totalVersionCount: appListing.totalVersionCount,
          versionHistoryUrl: appListing.versionHistoryUrl
        }) : null,
        lozenges: showHeaderLozenges ? makeHeaderLozenges({
          supportDetails: appListing.supportDetails,
          partnerName: appListing.partner.name,
          supportLink: supportTabLink,
          categories: appListing.categories,
          marketingLabels: appListing.marketingLabels,
          onMigrationLozengeClick,
          cloudMigration,
          showCloudMigrationLozenge,
          isServerAppEos,
          showNewJiraSoftwareLabelFlag,
          showAetherAppListingPage
        }) : [],
        selectedTab: tabSelected.key,
        onTabChange: handleTabChange,
        tabsData: tabs
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(AppHeader, {
          appHeaderDetails: appHeaderDetails,
          appRatingLink: links.appRating,
          appIconLink: iconLink,
          bottomSeparator: false,
          headerInjection: headerInjection,
          isAppCloudFortified: isAppCloudFortified,
          showInstallationCount: showInstallationCount,
          openMarketplaceLinksInNewTab: openMarketplaceLinksInNewTab,
          bugBountyParticipant: (_appListing$programs3 = appListing.programs) === null || _appListing$programs3 === void 0 ? void 0 : _appListing$programs3.bugBountyParticipant,
          compatibilitySummary: showHeaderProductCompatibilities ? /*#__PURE__*/_jsx(ProductCompatibilitySummary, {
            cloudCompatibleProducts: appListing.cloudCompatibleProducts,
            serverCompatibleProducts: appListing.serverCompatibleProducts,
            dataCenterCompatibleProducts: appListing.dataCenterCompatibleProducts,
            totalVersionCount: appListing.totalVersionCount,
            versionHistoryUrl: appListing.versionHistoryUrl
          }) : null,
          lozenges: showHeaderLozenges ? makeHeaderLozenges({
            supportDetails: appListing.supportDetails,
            partnerName: appListing.partner.name,
            supportLink: supportTabLink,
            categories: appListing.categories,
            marketingLabels: appListing.marketingLabels,
            onMigrationLozengeClick,
            cloudMigration,
            showCloudMigrationLozenge,
            isServerAppEos,
            showNewJiraSoftwareLabelFlag
          }) : []
        }), (_props$components3 = props.components) === null || _props$components3 === void 0 ? void 0 : _props$components3.afterHeader, /*#__PURE__*/_jsx(ResponsiveTabs, {
          id: ELEMENT_ID.APP_LISTING_TABS,
          selected: tabSelected.key,
          maxVisibleTabs: MAX_VISIBLE_TABS_CONFIG,
          onChange: handleTabChange,
          afterTabs: hostingSelect,
          tabs: tabs
        })]
      }), /*#__PURE__*/_jsx(AppStructuredData, {
        name: appListing.name,
        createdAt: appListing.createdAt,
        description: appListing.summary,
        icon: appListing.logo,
        partner: {
          name: appListing.partner.name,
          url: appListing.partner.url
        },
        review: {
          rating: appListing.reviewSummary.rating,
          count: appListing.reviewSummary.count
        },
        compatibleProducts: appListing.compatibleProducts
      })]
    })
  });
};
export default AppListing;